import { createStackNavigator } from "@react-navigation/stack";
import UrgentRequest from "@src/screens/Tenant/TenancyRequests/UrgentRequest";
import CreateRequest from "@src/screens/Tenant/TenancyRequests/CreateRequest";
import RequestDetails from "@src/screens/Tenant/TenancyRequests/RequestDetails";
import { useTranslation } from "react-i18next";
import { NavigatorScreenParams, useRoute } from "@react-navigation/core";
import { TenantTabsParamsList } from "../TenancyNavigator/TenantTabScreens";
import GeneralRequest from "@src/screens/Tenant/TenancyRequests/GeneralRequest";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { RequestsParamList } from "../RootStack";
import { RequestContext } from "./useRequests";
import { useGlobalTenant } from "../hooks/useGlobalTenant";
import VacateRequest from "@src/screens/Tenant/TenancyRequests/VacateRequest";
import { pendoBlankLabels, pendoProfileTypeLabel, updatePendoWithNewDetails } from "@src/lib/Analytics/Pendo/pendoUtils";
import { useGetAllTenancies } from "./useGetAllTenancies";
import { useTenantFeatureFlags } from "@src/screens/Tenant/hooks/useTenantFeatureFlags";
import { useUpdateFeatureFlags } from "@src/lib/FeatureFlags";

export type RequestsStackParamsList = {
  Root: undefined;
  Tenancy: NavigatorScreenParams<TenantTabsParamsList>;
  CreateRequest: { tenancyId?: number };
  UrgentRequest: undefined;
  GeneralRequest: { tenancyId?: number };
  VacateRequest: { tenancyId?: number };
  RequestDetails: { workflowId: number; tenancyId: number };
};

const RequestsStack = createStackNavigator<RequestsStackParamsList>();
export const RequestsNavigator = (paramList: RequestsParamList) => {
  const nav = useRoute();
  const { handleSetTenancyId } = useGlobalTenant();
  const { data: tenancyData } = useGetAllTenancies();
  const { t } = useTranslation("Tenant", { keyPrefix: "Requests" });

  const [currentTenancyId, setCurrentTenancyId] = useState(undefined);
  const updateFeatureFlag = useUpdateFeatureFlags();
  const { data: featureFlags } = useTenantFeatureFlags(currentTenancyId);

  // the first tenancyId prop is retrieved when using react-native navigation's navigate prop.
  // the second prop is used during deep linking

  // const tenancyId = useMemo(() => {

  // }, [paramList.route.params])

  const displayedTenancy = useMemo(() => {
    if (!tenancyData?.length || currentTenancyId == null) return undefined;
    return tenancyData.find((t) => t.tenancyId == currentTenancyId);
  }, [tenancyData, currentTenancyId]);

  // below function is a temporary solution for inconsistent params structure passed in during navigation
  const utilGetTenancyIdFromParamListOrNav = (paramList: IntentionalAny<"any">, nav: IntentionalAny<"any">) => {
    let tenancyId;
    if (paramList?.params?.tenancyId) {
      // console.log("retrieved from 1: paramList?.params?.tenancyId", paramList?.params?.tenancyId);
      tenancyId = paramList?.params?.tenancyId;
    } else if (paramList?.params?.params?.tenancyId) {
      // console.log("retrieved from 2: paramList?.params?.params?.tenancyId", paramList?.params?.params?.tenancyId);
      tenancyId = paramList?.params?.params?.tenancyId;
    } else if (nav?.params?.tenancyId) {
      // console.log("retrieved from 3: nav.params?.tenancyId", nav.params?.tenancyId);
      tenancyId = nav?.params?.tenancyId;
    } else if (nav?.params?.params?.tenancyId) {
      // console.log("retrieved from 4: nav.params?.params?.tenancyId", nav.params?.params?.tenancyId);
      tenancyId = nav?.params?.params?.tenancyId;
    } else {
      tenancyId = undefined;
    }

    return tenancyId;
  };
  useLayoutEffect(() => {
    if (!paramList && !nav) return;

    if (!currentTenancyId) {
      const _currentTenancyId = utilGetTenancyIdFromParamListOrNav(paramList, nav);
      setCurrentTenancyId(_currentTenancyId);
      handleSetTenancyId(_currentTenancyId);
    }

    if (!displayedTenancy) return;

    updatePendoWithNewDetails(
      pendoProfileTypeLabel.tenant,
      displayedTenancy.tenantProfileEntityId ?? pendoBlankLabels.blankUserProfileId,
      displayedTenancy.companyName ?? pendoBlankLabels.blankCompany,
      displayedTenancy.agencyName ?? pendoBlankLabels.blankAgency,
      displayedTenancy.officeName ?? pendoBlankLabels.blankOffice
    );
  }, [
    paramList,
    nav,
    displayedTenancy?.tenantProfileEntityId,
    displayedTenancy?.companyName,
    displayedTenancy?.agencyName,
    displayedTenancy?.officeName
  ]);

  // useEffect(() => {
  //   if (currentTenancyId) {
  //     handleSetTenancyId(currentTenancyId);
  //   }
  // }, [currentTenancyId]);

  useEffect(() => {
    if (featureFlags) {
      updateFeatureFlag(featureFlags);
    }
  }, [featureFlags, updateFeatureFlag]);

  // useEffect(() => {
  //   console.log("paramList log", paramList);
  //   console.log("nav.params log", nav);
  //   // console.log("tenancyId log", tenancyId);
  // }, []);

  useEffect(() => {
    if (featureFlags) {
      updateFeatureFlag(featureFlags);
    }
  }, [featureFlags, updateFeatureFlag]);

  // useEffect(() => {
  //   console.log("paramList log", paramList);
  //   console.log("nav.params log", nav);
  //   // console.log("tenancyId log", tenancyId);
  // }, []);

  const contextValue = useMemo(() => {
    return {
      ...paramList,
      //@ts-ignore - how do we type dynamic params (second params property)?
      tenancyId: currentTenancyId
    };
  }, [paramList, currentTenancyId]);

  return (
    <RequestContext.Provider value={contextValue}>
      <RequestsStack.Navigator screenOptions={{ headerShown: false }}>
        <RequestsStack.Screen name="CreateRequest" component={CreateRequest} options={{ title: t("NewRequest") }} />
        <RequestsStack.Screen name="GeneralRequest" component={GeneralRequest} options={{ title: t("RequestFormHeading") }} />
        <RequestsStack.Screen name="VacateRequest" component={VacateRequest} options={{ title: t("VacateRequestFormHeading") }} />
        <RequestsStack.Screen name="UrgentRequest" component={UrgentRequest} options={{ title: t("UrgentRequestHeading") }} />
        <RequestsStack.Screen name="RequestDetails" component={RequestDetails} options={{ title: t("RequestDetailsHeading") }} />
      </RequestsStack.Navigator>
    </RequestContext.Provider>
  );
};
